/**
 * AppFooter — iter250 mass-market rewrite
 *
 * Goals (no backend changes):
 *   1. Group 22 flat links into 4 themed columns (Платформа / Здраве / Бизнес / Правни)
 *   2. Soften "Предназначение" + "Disclaimer" — single condensed compliance row
 *   3. Visual hierarchy: brand name + tagline → columns → bottom strip
 *   4. Mobile: collapse columns into accordion (clean stack)
 *   5. Keep all destinations & test ids intact
 */
import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { ShieldCheck } from "lucide-react";
import { useComplianceCatalog } from "@/lib/compliance";

export default function AppFooter({ compact: _compact, hideCompliance = false }) {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const { catalog } = useComplianceCatalog();
  const bg = language === "bg";

  const linkCls = `text-xs ${isDark ? "text-gray-400 hover:text-white" : "text-[#64748B] hover:text-[#1A1A2E]"} transition-colors`;
  const colTitleCls = `text-[10px] font-bold uppercase tracking-[0.18em] mb-3 ${isDark ? "text-gray-300" : "text-[#1A1A2E]"}`;

  // ─── 4 themed columns ───
  const columns = [
    {
      key: "platform",
      title: bg ? "Платформа" : "Platform",
      links: [
        { to: "/pricing",  label: bg ? "Цени" : "Pricing" },
        { to: "/compare",  label: bg ? "Сравнение" : "Compare", testId: "footer-compare-link" },
        { to: "/guide",    label: bg ? "Ръководство" : "Guide" },
        { to: "/faq",      label: "FAQ" },
      ],
    },
    {
      key: "health",
      title: bg ? "Безплатни инструменти" : "Free tools",
      links: [
        { to: "/anonymous-lab",   label: bg ? "Анонимен анализ" : "Anonymous lab analysis" },
        { to: "/health-intelligence/breast",   label: bg ? "Рак на гърдата" : (language === "es" ? "Salud mamaria" : "Breast Health"), testId: "footer-breast-health-link" },
        { to: "/tools",           label: bg ? "Всички инструменти" : "All tools" },
      ],
    },
    {
      key: "company",
      title: bg ? "Компания" : "Company",
      links: [
        { to: "/about",     label: bg ? "За нас" : "About" },
        { to: "/contact",   label: bg ? "Контакт" : "Contact" },
      ],
    },
    {
      key: "legal",
      title: bg ? "Доверие и съответствие" : "Trust & compliance",
      links: [
        { to: "/compliance", label: bg ? "Live compliance" : "Live compliance", testId: "footer-compliance-link" },
        { to: "/legal", label: bg ? "Политики" : "Policies" },
        { to: "/legal/ai-transparency", label: bg ? "AI прозрачност" : "AI Transparency", testId: "footer-ai-transparency-link" },
        { to: "/legal/ai-risk-safety", label: bg ? "AI риск и безопасност" : "AI Risk & Safety", testId: "footer-ai-risk-link" },
        { to: "/legal/clinical-governance", label: bg ? "Клинично управление" : "Clinical Governance", testId: "footer-clinical-gov-link" },
        { to: "/legal/limitations", label: bg ? "Ограничения" : "Limitations", testId: "footer-limitations-link" },
      ],
    },
  ];

  return (
    <footer
      className={`mt-auto border-t ${isDark ? "bg-[hsl(222,47%,7%)] border-[hsl(223,47%,15%)]" : "bg-white border-[#E2E8F0]"}`}
      data-testid="app-footer"
    >
      {/* ─── Compliance condensed row (replaces verbose Intended Use + Disclaimer) ─── */}
      {!hideCompliance && (
      <div
        data-testid="footer-intended-use"
        className={`px-4 sm:px-6 py-3 border-b ${isDark ? "border-[hsl(223,47%,15%)] bg-[hsl(222,47%,9%)]" : "border-[#F1F5F9] bg-sky-50/40"}`}
      >
        <div className="max-w-7xl mx-auto flex items-start gap-2.5">
          <ShieldCheck className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${isDark ? "text-sky-400" : "text-sky-600"}`} />
          <div>
            {/* iter592 — Task 6: позитивното позициониране води, дисклеймърът е под него */}
            <p className={`text-xs font-semibold ${isDark ? "text-white" : "text-sky-950"}`}>
              {bg
                ? "Nutramilo е AI платформа за персонализирано здраве и превенция."
                : "Nutramilo is an AI platform for personalised health and prevention."}
            </p>
            <p className={`text-[11px] mt-0.5 ${isDark ? "text-gray-400" : "text-sky-800"}`}>
              {bg
                ? "Не поставя диагнози и не замества медицинска консултация."
                : "It does not diagnose and does not replace medical consultation."}
            </p>
            <p className={`text-[10px] leading-relaxed mt-1.5 ${isDark ? "text-gray-500" : "text-sky-700/70"}`}>
              <span className="font-semibold">{bg ? "Предназначение:" : "Intended use:"}</span>{" "}
              {bg
                ? "Платформата предоставя образователни анализи, оценки и препоръки, базирани на хранене, начин на живот и научнообосновани модели. Nutramilo не е медицинско изделие и не предписва лечение. AI генерира обяснения и персонализирани насоки, които не заместват консултацията с лекар или друг квалифициран здравен специалист."
                : (catalog?.intended_use || "It provides educational analyses, assessments and recommendations based on nutrition, lifestyle and science-based models. Nutramilo is not a medical device and does not prescribe treatment. AI generates explanations and personalised guidance that do not replace consultation with a physician or another qualified health professional.")}
            </p>
          </div>
        </div>
      </div>
      )}

      {/* ─── Brand + 4 columns ─── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-10">
          {/* Brand block */}
          <div className="md:col-span-4">
            <Link to="/" className="inline-flex items-center gap-2 mb-3 group" aria-label="Nutramilo">
              <img
                src="/nutramilo-logo.png"
                alt="Nutramilo"
                className="h-10 sm:h-12 w-auto select-none group-hover:opacity-80 transition-opacity"
                data-testid="footer-brand-logo"
                draggable="false"
              />
            </Link>
            <p className={`text-xs leading-relaxed max-w-xs ${isDark ? "text-gray-400" : "text-[#4A4A68]"}`}>
              {bg
                ? "AI платформа за персонализирано здраве и превенция — изградена с реална клинична практика."
                : "AI platform for personalised health and prevention — built with real clinical practice."}
            </p>
            <p className={`text-[10px] mt-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
              {bg ? "Хостинг в Европейския съюз · GDPR" : "EU-based hosting · GDPR-aligned"}
            </p>
          </div>

          {/* 4 link columns */}
          <div className="md:col-span-8 grid grid-cols-2 sm:grid-cols-4 gap-6">
            {columns.map((col) => (
              <div key={col.key} data-testid={`footer-col-${col.key}`}>
                <p className={colTitleCls}>{col.title}</p>
                <ul className="space-y-2">
                  {col.links.map((l) => (
                    <li key={l.to}>
                      <Link to={l.to} className={linkCls} {...(l.testId ? { "data-testid": l.testId } : {})}>
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* ─── Bottom strip: copyright only (iter1121 — compliance лентата премахната по заявка на потребителя) ─── */}
        <div className={`pt-6 border-t flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 ${isDark ? "border-[hsl(223,47%,15%)]" : "border-[#F1F5F9]"}`}>
          <span />
          <p className={`text-[11px] ${isDark ? "text-gray-500" : "text-gray-400"}`}>
            © {new Date().getFullYear()} Nutramilo · International Sci Ink Press Ltd.
          </p>
        </div>
      </div>
    </footer>
  );
}
